import * as React from 'react';
import './scss/input.scss';
import { CmdError } from '@commander-services/gui-components';
import TooltipComponent from '../../TooltipComponent';
import { ISimpleSelectOption } from '../ArrayFields/interfaces';

export type TInputType = 'color' | 'text' | 'number' | 'password' | 'hidden' | 'email';

interface IInputProps {
  id: string;
  label: string;
  name: string;
  type: TInputType;
  placeholder?: string;
  readonly?: boolean;
  className?: string;
  required?: boolean;
  allowOnlyNumbers?: boolean;
  onChangeCallback?: (value: any) => void;
  onBlurCallBack?: (value: string) => void;
  tooltip?: string;
  step?: number | string;
  warningMessage?: string;
  labelClassName?: string;
  rowClassName?: string;
  columnClassName?: string;
  disabled?: boolean;
  min?: number;
  noDecimal?: boolean;
  isChangeDetail?: string;
  currency?: string;
  options?: ISimpleSelectOption[];
  selectedItems?: string[];
  handleCheckboxesOnChange?: (values: string[]) => void;
  infoMessage?: string;
  touched?: boolean;
  error?: string;
  setValue?: (value: string) => void;
  value: string;
}

function Input(props: IInputProps): JSX.Element {
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  let errBorder = '';
  if (props.touched && props.error) {
    errBorder = 'err-border';
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const re = /^[+0-9\b]+$/;
    if (value === '' || (props.allowOnlyNumbers && re.test(value))) {
      if (props.setValue) {
        props.setValue(event.currentTarget.value);
      }
      if (props.onChangeCallback) {
        props.onChangeCallback(value);
      }
    }
    if (!props.allowOnlyNumbers) {
      if (props.setValue) {
        props.setValue(event.currentTarget.value);
      }
      if (props.onChangeCallback) {
        props.onChangeCallback(value);
      }
    }
  };

  const handleDecimal = (event: any) => {
    if (event.charCode < 48 || event.charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  React.useEffect(() => {
    if (props.selectedItems) {
      setSelectedItems(props.selectedItems);
    }
  }, [props.selectedItems]);

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (props.onBlurCallBack) {
      if (props.setValue) {
        props.setValue(event.currentTarget.value);
      }
      props.onBlurCallBack(props.value);
    }
  };

  const handleCheckboxesOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.currentTarget;
    let values = JSON.parse(JSON.stringify(selectedItems));
    if (checked) {
      values.push(String(value));
      setSelectedItems(values);
    } else {
      values = selectedItems.filter((i: string) => i !== value);
      setSelectedItems(values);
    }
    if (props.handleCheckboxesOnChange) {
      props.handleCheckboxesOnChange(values);
    }
  };

  return (
    <div
      className={`input-component form-group ${props.touched && props.error && 'form-group-error'}`}
    >
      <div className={props.rowClassName || ''}>
        <label htmlFor={props.id} className={`${props.labelClassName || 'd-block form-label'}`}>
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className={props.columnClassName || 'd-block'}>
          {props.isChangeDetail ? (
            <div
              style={{
                background: 'rgb(242, 242, 242)',
                border: '1px solid rgb(221, 221, 221)',
                minHeight: '30px',
                color: '#B3B3B3',
                borderRadius: '4px',
                padding: '7px',
              }}
            >
              <TooltipComponent
                id={props.id}
                firstItems={true}
                width="180px"
                positionRight={true}
                overlay={<div style={{ color: '#fff' }}>{props.isChangeDetail}</div>}
              >
                <span
                  style={{
                    background: 'rgb(61, 114, 200)',
                    padding: '2px 10px 3px 10px',
                    borderRadius: '11px',
                    opacity: 0.3,
                    color: 'rgb(0, 0, 0)',
                  }}
                >
                  {props.value}
                </span>
              </TooltipComponent>
            </div>
          ) : (
            <div className={props.currency ? 'form-control-suffix' : props.options ? 'row' : ''}>
              <div className={props.options ? 'inputWithCheckboxes' : ''}>
                <input
                  data-cy={`form-input-${props.name}`}
                  type={props.type}
                  placeholder={props.placeholder || ''}
                  onChange={handleOnChange}
                  name={props.name}
                  id={props.id}
                  value={props.value}
                  readOnly={props.readonly || false}
                  className={`${props.className} form-control ${errBorder}`}
                  title={props.tooltip || ''}
                  step={props.step || undefined}
                  onBlur={handleOnBlur}
                  disabled={!!props.disabled}
                  style={props.disabled ? { cursor: 'not-allowed', color: '#999' } : {}}
                  min={props.min}
                  onKeyPress={props.noDecimal ? handleDecimal : undefined}
                />
                {props.currency && <span className="actions">{props.currency}</span>}
                {props.options && props.touched && props.error && (
                  <CmdError message={props.error} />
                )}
                {props.options && props.warningMessage && (
                  <CmdError message={props.warningMessage} />
                )}
              </div>
              {props.options && (
                <div
                  className="pull-left"
                  style={{
                    marginTop: '3px',
                    marginBottom: '3px',
                    paddingRight: '15px',
                    paddingLeft: '15px',
                  }}
                >
                  {props.options.map((option: ISimpleSelectOption, index: number) => (
                    <div
                      className="e-checkbox"
                      style={{ marginRight: '8px' }}
                      key={`checkbox-${props.name}-${index}`}
                    >
                      {option.tooltip && (
                        <TooltipComponent
                          id={props.id}
                          firstItems={true}
                          width="180px"
                          positionLeft={true}
                          positionRight={false}
                          top="27px"
                          display="inline"
                          overlay={<div style={{ color: '#fff' }}>{option.tooltip || ''}</div>}
                        >
                          <label data-cy={`multipleCheckboxes-${option.value}`}>
                            <input
                              type="checkbox"
                              disabled={props.disabled || option.disabled || false}
                              value={option.value}
                              onChange={handleCheckboxesOnChange}
                              data-cy={`checkboxGroup-${props.name}-${option.value}`}
                              checked={
                                selectedItems &&
                                selectedItems.length > 0 &&
                                selectedItems.includes(String(option.value))
                              }
                              data-value={option.value}
                              data-disabled={option.disabled}
                            />
                            <span
                              style={{
                                fontSize: '13px',
                                fontFamily: 'Open Sans, sans-serif',
                              }}
                            >
                              {option.item}
                            </span>
                          </label>
                        </TooltipComponent>
                      )}
                      {!option.tooltip && (
                        <label data-cy={`multipleCheckboxes-${option.value}`}>
                          <input
                            type="checkbox"
                            disabled={props.disabled || option.disabled || false}
                            value={option.value}
                            onChange={handleCheckboxesOnChange}
                            data-cy={`checkboxGroup-${props.name}-${option.value}`}
                            checked={
                              selectedItems &&
                              selectedItems.length > 0 &&
                              selectedItems.includes(String(option.value))
                            }
                            data-value={option.value}
                            data-disabled={option.disabled}
                          />
                          <span
                            style={{
                              fontSize: '13px',
                              fontFamily: 'Open Sans, sans-serif',
                            }}
                          >
                            {option.item}
                          </span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!props.options && props.touched && props.error && <CmdError message={props.error} />}
          {!props.options && props.warningMessage && <CmdError message={props.warningMessage} />}
          {!props.options && props.infoMessage && (
            <CmdError message={props.infoMessage} isInfo={true} />
          )}
        </div>
      </div>
    </div>
  );
}

export default Input;
